import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import assistantService, {
  streamAssistantMessage,
  submitElicitation,
  isAssistantQuotaStreamError,
} from '@/services/assistant.service';
import assistantQuotaService from '@/services/assistantQuota.service';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  AssistantAttachment,
  AssistantConversationDetail,
  AssistantConversationListItem,
  AssistantMemoryResponse,
  AssistantMessage,
  AssistantStreamHandlers,
  AssistantStreamStatus,
  AssistantSummary,
  CompanyOption,
  ElicitationAnswer,
  GeneratedFileRef,
  MyGeneralQuota,
  PendingElicitation,
  WebCitation,
} from '@/types/assistant';
import {
  ASSISTANT_QUOTA_ERROR_EVENT,
  AssistantQuotaErrorEventDetail,
  clearAssistantQuotaModal,
} from '@/utils/assistantQuotaErrorHandler';
import { attachmentErrorMessage } from '@/utils/attachmentError';
import AssistantChat from '@/components/assistant/AssistantChat';
import AssistantConversationSidebar from '@/components/assistant/AssistantConversationSidebar';
import AssistantMemoryPanel from '@/components/assistant/AssistantMemoryPanel';
import AssistantQuotaSummary from '@/components/assistant/AssistantQuotaSummary';
import AssistantQuotaModal from '@/components/assistant/AssistantQuotaModal';
import LoadingSpinner from '@/components/LoadingSpinner';


// J07: sugestões do empty-state pros assistentes confidenciais (jurídico).
const LEGAL_SUGGESTIONS = [
  'Anexe um contrato e peça: "analise este contrato"',
  'Quais são as cláusulas de força maior e suspensão de pagamento?',
  'Exporte a análise em planilha',
  'Em caso de incêndio, posso suspender o pagamento?',
];

// S06: sugestões do empty-state do bot SharePoint (H.Find).
const SHAREPOINT_SUGGESTIONS = [
  'Anexe o DRL (.xlsx) e peça: "confira o DRL na pasta"',
  'Quais documentos estão faltando?',
  'Exporte o DRL anotado em planilha',
  'Re-indexe a pasta do SharePoint',
];

// S06: mensagens das ações rápidas do H.Find — disparam as tools via chat.
const EXPORT_DRL_MESSAGE = 'Exporte o DRL anotado em uma planilha Excel.';
const REINDEX_MESSAGE = 'Re-indexe a pasta do SharePoint, forçando a atualização do índice.';

// D04: sugestões do empty-state do bot de apresentações (H.Slide).
const DECK_SUGGESTIONS = [
  'Monte uma apresentação de 8 slides sobre nossos resultados de 2026',
  'Anexe um texto/PDF e peça: "transforme isto numa apresentação"',
  'Crie um pitch de vendas pra diretoria em 6 slides',
  'Faça slides de boas-vindas pra novos colaboradores',
];

// Q06: sugestões do empty-state do bot de revisão de documentos (H.Rev).
const QUALITY_SUGGESTIONS = [
  'Anexe o POP e peça: "revise este documento"',
  'Quais apontamentos de formatação foram encontrados?',
  'Exporte a devolutiva em Word',
  'As legislações citadas estão vigentes?',
];

// Q06: mensagem da ação rápida "Exportar devolutiva" do H.Rev — dispara a tool via chat.
const EXPORT_REVIEW_MESSAGE = 'Exporte a devolutiva da revisão em um arquivo Word (.docx).';

/** True se o assistente tem a capacidade (feature ligada no tool_config — S06). */
function hasFeature(asst: AssistantSummary | null, feature: string): boolean {
  return !!asst?.features?.includes(feature);
}

function toListItem(c: AssistantConversationDetail): AssistantConversationListItem {
  return {
    id: c.id,
    title: c.title,
    last_message_at: c.last_message_at,
    archived: c.archived,
    created_at: c.created_at,
    updated_at: c.updated_at,
  };
}

const AssistantPage: FC = () => {
  const { user } = useAuthContext();
  const { agentId: routeAgentId } = useParams<{ agentId?: string }>();
  const greetingName = useMemo(() => user?.name?.trim().split(/\s+/)[0] || 'por aqui', [user]);

  // J07: assistente ativo (H.Bot, H.Lex, H.Find…). O ref evita closure stale nos callbacks.
  const [activeAssistant, setActiveAssistant] = useState<AssistantSummary | null>(null);
  const activeAssistantIdRef = useRef<string | undefined>(undefined);
  // Especializado (não-default) → esconde o seletor de empresa (G14). Capacidades por feature:
  // jurídico (contratos retidos + walkthrough legal) vs SharePoint (DRL + ações + walkthrough).
  const confidential = !!activeAssistant && !activeAssistant.is_default;
  const isLegal = hasFeature(activeAssistant, 'contract_analysis');
  const isSharepoint = hasFeature(activeAssistant, 'sharepoint_match');
  // D04: H.Slide (apresentações) — não-default, mas GERA material branded, então mantém o
  // seletor de empresa (G14) e tem walkthrough próprio do deck.
  const isDeck = hasFeature(activeAssistant, 'create_presentation');
  // Q06: H.Rev (revisão de documentos) — confidencial (documentos internos), com
  // contratos/anexos retidos + walkthrough e barra de ações próprios.
  const isQuality = hasFeature(activeAssistant, 'document_review');

  const [conversations, setConversations] = useState<AssistantConversationListItem[]>([]);
  const [activeConv, setActiveConv] = useState<AssistantConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);

  // J07/fix: trilha ACUMULADA dos status da análise jurídica (category=legal). Acumulada
  // por updater funcional (à prova de batching) pra mostrar o raciocínio real do bot.
  const [legalSteps, setLegalSteps] = useState<AssistantStreamStatus[]>([]);
  const [contracts, setContracts] = useState<AssistantAttachment[]>([]);
  const [deletingContractId, setDeletingContractId] = useState<string | null>(null);

  const [sending, setSending] = useState(false);
  const [awaitingFirstToken, setAwaitingFirstToken] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // E01/E02: pergunta de elicitação pendente do bot (ask_user) → renderiza o card e
  // bloqueia o input. Vem do evento SSE `question` (live) OU do detail (durável, reabrir).
  const [pendingElicitation, setPendingElicitation] = useState<PendingElicitation | null>(null);

  const [quota, setQuota] = useState<MyGeneralQuota | null>(null);

  // G14: catálogo de marcas + empresa ativa da conversa (geração branded).
  const [companies, setCompanies] = useState<CompanyOption[]>([]);

  const [memory, setMemory] = useState<AssistantMemoryResponse | null>(null);
  const [memoryLoading, setMemoryLoading] = useState(false);

  const [pendingAttachments, setPendingAttachments] = useState<AssistantAttachment[]>([]);
  const [uploading, setUploading] = useState(false);

  const [convDrawerOpen, setConvDrawerOpen] = useState(false);
  const [memoryDrawerOpen, setMemoryDrawerOpen] = useState(false);
  // Desktop: conversas recolhidas (persistido) — dá espaço ao chat. O painel direito
  // (memória + cota) fica fechado por padrão e abre por botão (memoryDrawerOpen).
  const [convCollapsed, setConvCollapsed] = useState<boolean>(
    () => localStorage.getItem('assistant_conv_collapsed') === '1',
  );
  const toggleConvCollapsed = useCallback(() => {
    setConvCollapsed((v) => {
      const next = !v;
      localStorage.setItem('assistant_conv_collapsed', next ? '1' : '0');
      return next;
    });
  }, []);

  const [quotaError, setQuotaError] = useState<AssistantQuotaErrorEventDetail['payload'] | null>(
    null,
  );

  const abortRef = useRef<AbortController | null>(null);
  const messages = useMemo<AssistantMessage[]>(() => activeConv?.messages ?? [], [activeConv]);
  const thinking = sending && (awaitingFirstToken || activeTool !== null);

  const abortInflight = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const refreshQuota = useCallback(async () => {
    try {
      const q = await assistantQuotaService.getMyQuota();
      setQuota(q);
    } catch {
      /* sem cota — selector some, chat segue */
    }
  }, []);

  const loadMemory = useCallback(async () => {
    setMemoryLoading(true);
    try {
      setMemory(await assistantService.getMemory());
    } catch {
      /* painel mostra vazio */
    } finally {
      setMemoryLoading(false);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    const list = await assistantService.listConversations(activeAssistantIdRef.current);
    setConversations(list);
    return list;
  }, []);

  // J07/Q06: anexos retidos da conversa — jurídico (contratos) e revisão de documentos
  // (POPs) compartilham o mesmo tratamento de confidencialidade; não pro SharePoint/deck.
  const loadContracts = useCallback(
    async (convId: string) => {
      if (!isLegal && !isQuality) return;
      try {
        setContracts(await assistantService.listAttachments(convId));
      } catch {
        setContracts([]);
      }
    },
    [isLegal, isQuality],
  );

  const openConversation = useCallback(
    async (convId: string) => {
      abortInflight();
      setError(null);
      setLegalSteps([]);
      setPendingAttachments([]);
      const detail = await assistantService.getConversation(convId);
      setActiveConv(detail);
      // E02: estado durável — reabrir uma conversa com pergunta pendente mostra o card.
      setPendingElicitation(detail.pending_elicitation ?? null);
      void loadContracts(convId);
    },
    [abortInflight, loadContracts],
  );

  const createConversation = useCallback(async () => {
    abortInflight();
    setError(null);
    setLegalSteps([]);
    setContracts([]);
    setPendingAttachments([]);
    setPendingElicitation(null);
    const created = await assistantService.createConversation(activeAssistantIdRef.current);
    setActiveConv(created);
    setConversations((prev) => [toListItem(created), ...prev]);
  }, [abortInflight]);

  // G14: catálogo de marcas (best-effort — sem ele o indicador some, não quebra).
  const loadCompanies = useCallback(async () => {
    try {
      setCompanies(await assistantService.listCompanies());
    } catch {
      setCompanies([]);
    }
  }, []);

  // G14: troca a empresa ativa da conversa (endpoint leve, sem custar turno de LLM).
  const handleSelectCompany = useCallback(
    async (companyId: string) => {
      if (!activeConv) return;
      try {
        const updated = await assistantService.setConversationCompany(activeConv.id, companyId);
        setActiveConv((prev) =>
          prev && prev.id === updated.id
            ? { ...prev, active_company: updated.active_company, active_company_name: updated.active_company_name }
            : prev,
        );
      } catch {
        setError('Não consegui trocar a empresa agora. Tenta de novo?');
      }
    },
    [activeConv],
  );

  // Boot: cota + memória + conversas (abre a mais recente ou cria uma).
  useEffect(() => {
    // Kill-switch (G12): flag global desligada → não bota (evita 403) e a tela
    // de "indisponível" assume no render.
    if (user && user.general_agent_enabled === false) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function boot() {
      try {
        setLoading(true);
        void refreshQuota();
        void loadMemory();
        void loadCompanies();

        // J07: resolve o assistente ativo (rota :agentId → default H.Bot → 1º acessível).
        let asst: AssistantSummary[] = [];
        try {
          const r = await assistantService.listAssistants();
          if (Array.isArray(r)) asst = r;
        } catch {
          /* sem assistentes acessíveis (flag off / sem acesso) — cai pro H.Bot default */
        }
        if (cancelled) return;
        const active =
          asst.find((a) => a.id === routeAgentId) || asst.find((a) => a.is_default) || asst[0] || null;
        setActiveAssistant(active);
        activeAssistantIdRef.current = active?.id;

        const list = await assistantService.listConversations(active?.id);
        if (cancelled) return;
        setConversations(list);
        if (list.length > 0) {
          const detail = await assistantService.getConversation(list[0].id);
          if (!cancelled) {
            setActiveConv(detail);
            setPendingElicitation(detail.pending_elicitation ?? null);
            if (hasFeature(active, 'contract_analysis') || hasFeature(active, 'document_review')) {
              void loadContracts(list[0].id);
            }
          }
        } else {
          const created = await assistantService.createConversation(active?.id);
          if (!cancelled) {
            setActiveConv(created);
            setConversations([toListItem(created)]);
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setBootError('Não foi possível carregar o assistente.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    boot();
    return () => {
      cancelled = true;
      abortInflight();
    };
  }, [refreshQuota, loadMemory, loadCompanies, loadContracts, abortInflight, user, routeAgentId]);

  // Modal de cota (DT-12): stream/interceptor disparam o evento.
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<AssistantQuotaErrorEventDetail>).detail;
      if (detail) setQuotaError(detail.payload);
    };
    window.addEventListener(ASSISTANT_QUOTA_ERROR_EVENT, handler);
    return () => window.removeEventListener(ASSISTANT_QUOTA_ERROR_EVENT, handler);
  }, []);

  async function handleAttachFiles(files: File[]) {
    if (!activeConv) return;
    setUploading(true);
    try {
      for (const file of files) {
        try {
          const att = await assistantService.uploadAttachment(activeConv.id, file);
          setPendingAttachments((prev) => [...prev, att]);
          if (isLegal || isQuality) setContracts((prev) => [att, ...prev]); // já fica retido (J07/Q06)
        } catch (err) {
          if (!isAssistantQuotaStreamError(err)) {
            setError(attachmentErrorMessage(file.name, err));
          }
          // 402 já abriu o modal via interceptor.
        }
      }
    } finally {
      setUploading(false);
    }
  }

  async function handleRemoveAttachment(id: string) {
    if (!activeConv) return;
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
    try {
      await assistantService.deleteAttachment(activeConv.id, id);
    } catch {
      /* já removido da UI; backend purga sozinho em 30d */
    }
  }

  function handleStop() {
    abortInflight();
    setSending(false);
    setAwaitingFirstToken(false);
    setActiveTool(null);
  }

  /**
   * Roda UM turno de streaming (enviar mensagem OU retomar elicitação). `starter` é
   * quem abre o stream — `streamAssistantMessage` ou `submitElicitation` (E02). Tudo o
   * resto (bolhas otimistas, handlers SSE, erros, pausa por `question`) é idêntico.
   */
  async function runStreamingTurn(
    userContent: string,
    starter: (convId: string, handlers: AssistantStreamHandlers, signal: AbortSignal) => Promise<void>,
  ) {
    if (!activeConv || sending) return;
    const convId = activeConv.id;

    const optimisticUserId = `optimistic-user-${Date.now()}`;
    const streamingAssistantId = `streaming-assistant-${Date.now()}`;
    const optimisticUser: AssistantMessage = {
      id: optimisticUserId,
      role: 'USER',
      content: userContent,
      tokens_in: 0,
      tokens_out: 0,
      model_used: '',
      metadata: {},
      created_at: new Date().toISOString(),
    };
    const streamingAssistant: AssistantMessage = {
      id: streamingAssistantId,
      role: 'ASSISTANT',
      content: '',
      tokens_in: 0,
      tokens_out: 0,
      model_used: '',
      metadata: {},
      created_at: new Date().toISOString(),
    };

    setSending(true);
    setAwaitingFirstToken(true);
    setActiveTool(null);
    setLegalSteps([]);
    setError(null);
    setActiveConv((prev) =>
      prev ? { ...prev, messages: [...prev.messages, optimisticUser, streamingAssistant] } : prev,
    );

    abortInflight();
    const controller = new AbortController();
    abortRef.current = controller;
    let serverErrorMsg: string | null = null;
    let paused = false; // E02: o bot pausou e perguntou (event `question`)
    const collectedCitations: WebCitation[] = [];
    const collectedFiles: GeneratedFileRef[] = [];

    try {
      await starter(
        convId,
        {
          onStatus: (status) => {
            setAwaitingFirstToken(true);
            // J07/S06/D04/Q06: status de domínio (análise jurídica, matching do DRL, geração
            // do deck ou revisão de documento) no thinking. Updater funcional: acumula TODOS
            // os status (à prova de batching do React).
            if (
              status?.category === 'legal' ||
              status?.category === 'sharepoint' ||
              status?.category === 'deck' ||
              status?.category === 'quality'
            ) {
              setLegalSteps((prev) => [...prev, status]);
            }
          },
          onToken: (chunk) => {
            setAwaitingFirstToken(false);
            setActiveTool(null);
            // Resposta começou a streamar → some a trilha (só recria array se havia passos).
            setLegalSteps((prev) => (prev.length ? [] : prev));
            setActiveConv((prev) =>
              prev
                ? {
                    ...prev,
                    messages: prev.messages.map((m) =>
                      m.id === streamingAssistantId ? { ...m, content: m.content + chunk } : m,
                    ),
                  }
                : prev,
            );
          },
          onTool: (evt) => {
            setActiveTool(evt.state === 'running' ? evt.name : null);
          },
          onFile: (evt) => {
            collectedFiles.push({ file_id: evt.file_id, filename: evt.filename, kind: evt.kind });
          },
          onCitation: (c) => collectedCitations.push(c),
          // E02: o bot pausou pra perguntar → renderiza o card e remove a bolha vazia.
          onQuestion: (payload) => {
            paused = true;
            setPendingElicitation({ questions: payload.questions, rounds: payload.rounds });
            setActiveConv((prev) =>
              prev
                ? { ...prev, messages: prev.messages.filter((m) => m.id !== streamingAssistantId) }
                : prev,
            );
          },
          onDone: (payload) => {
            setActiveConv((prev) =>
              prev
                ? {
                    ...prev,
                    messages: prev.messages.map((m) => {
                      if (m.id === optimisticUserId) return { ...m, id: payload.user_message_id };
                      if (m.id === streamingAssistantId) {
                        return {
                          ...m,
                          id: payload.message_id,
                          tokens_in: payload.tokens.in,
                          tokens_out: payload.tokens.out,
                          // Quem de fato respondeu este turno — vem do backend, não do que
                          // o usuário tivesse escolhido num seletor.
                          model_used: payload.model ?? m.model_used,
                          model_info: payload.model_info ?? m.model_info,
                          metadata: {
                            ...m.metadata,
                            citations: payload.citations ?? collectedCitations,
                            generated_files: payload.generated_files ?? collectedFiles,
                            // S06: resultado do DRL (H.Find) → renderiza o DrlResult na hora.
                            drl_result: payload.drl_result ?? null,
                            // D04: roteiro do deck (H.Slide) → renderiza o OutlinePreview.
                            outline: payload.outline ?? null,
                            // Q06: resultado da revisão (H.Rev) → renderiza o ReviewResult na hora.
                            review: payload.review ?? null,
                          },
                        };
                      }
                      return m;
                    }),
                  }
                : prev,
            );
          },
          onError: (payload) => {
            serverErrorMsg = payload.message || 'Tive um problema aqui. Tenta de novo?';
          },
        },
        controller.signal,
      );
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return; // cancelado pelo user
      console.error(err);
      if (isAssistantQuotaStreamError(err)) {
        serverErrorMsg = err.data.message; // modal já abriu (DT-12)
      } else if (!serverErrorMsg) {
        serverErrorMsg = 'Erro ao enviar mensagem. Tente de novo.';
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setAwaitingFirstToken(false);
      setActiveTool(null);
      setLegalSteps([]);
      setSending(false);
    }

    if (serverErrorMsg) {
      setError(serverErrorMsg);
      setActiveConv((prev) =>
        prev
          ? { ...prev, messages: prev.messages.filter((m) => m.id !== streamingAssistantId) }
          : prev,
      );
      return;
    }

    // Pausou aguardando resposta (card) → não recarrega lista/cota agora; o resume cuida.
    if (paused) return;

    void loadConversations();
    void refreshQuota();
    if (isLegal || isQuality) void loadContracts(convId); // anexo vira "retido" (J07/Q06)
  }

  async function handleSend(content: string) {
    if (!activeConv || sending) return;
    const attachmentIds = pendingAttachments.map((a) => a.id);
    setPendingAttachments([]);
    // Mensagem nova cancela qualquer pergunta pendente (o backend também trata assim).
    setPendingElicitation(null);
    await runStreamingTurn(content, (convId, handlers, signal) =>
      streamAssistantMessage(convId, content, handlers, { attachmentIds }, signal),
    );
  }

  // E02: responde a pergunta do bot e RETOMA o stream (esconde o card; o resume streama).
  async function handleElicitationSubmit(answers: ElicitationAnswer[]) {
    if (!activeConv || sending) return;
    const userContent = answers
      .map((a) => `${a.question}: ${[...a.selected, a.other ?? ''].filter(Boolean).join(', ')}`)
      .join('\n');
    setPendingElicitation(null);
    await runStreamingTurn(userContent, (convId, handlers, signal) =>
      submitElicitation(convId, answers, handlers, signal),
    );
  }

  // S06: ações rápidas do H.Find — disparam as tools via mensagem do chat.
  const handleExportDrl = () => void handleSend(EXPORT_DRL_MESSAGE);
  const handleReindex = () => void handleSend(REINDEX_MESSAGE);

  // Q06: ação rápida do H.Rev — dispara o export_review_report via mensagem do chat.
  const handleExportReview = () => void handleSend(EXPORT_REVIEW_MESSAGE);

  async function handleDeleteContract(id: string) {
    if (!activeConv) return;
    setDeletingContractId(id);
    try {
      await assistantService.deleteAttachment(activeConv.id, id);
      setContracts((prev) => prev.filter((c) => c.id !== id));
      setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch {
      setError('Não foi possível apagar o contrato.');
    } finally {
      setDeletingContractId(null);
    }
  }

  async function handleRename(id: string, title: string) {
    try {
      await assistantService.updateConversation(id, { title });
      setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
      setActiveConv((prev) => (prev && prev.id === id ? { ...prev, title } : prev));
    } catch {
      setError('Não foi possível renomear a conversa.');
    }
  }

  async function handleArchive(id: string) {
    try {
      await assistantService.archiveConversation(id);
      const remaining = conversations.filter((c) => c.id !== id);
      setConversations(remaining);
      if (activeConv?.id === id) {
        if (remaining.length > 0) await openConversation(remaining[0].id);
        else await createConversation();
      }
    } catch {
      setError('Não foi possível arquivar a conversa.');
    }
  }

  async function handleAddMemory(content: string) {
    try {
      await assistantService.addMemory(content);
      await loadMemory();
    } catch {
      setError('Não foi possível salvar na memória.');
    }
  }

  async function handleEditMemory(id: string, content: string) {
    try {
      await assistantService.updateMemory(id, content);
      await loadMemory();
    } catch {
      setError('Não foi possível editar a memória.');
    }
  }

  async function handleDeleteMemory(id: string) {
    try {
      await assistantService.deleteMemory(id);
      setMemory((prev) =>
        prev
          ? { ...prev, saved: prev.saved.map((m) => (m.id === id ? { ...m, is_active: false } : m)) }
          : prev,
      );
    } catch {
      setError('Não foi possível apagar a memória.');
    }
  }

  function closeQuotaModal() {
    setQuotaError(null);
    clearAssistantQuotaModal();
  }

  if (user && user.general_agent_enabled === false) {
    return (
      <div
        className="flex h-[calc(100vh-4rem)] lg:h-screen flex-col items-center justify-center text-center px-6"
        data-testid="assistant-disabled"
      >
        <span className="material-symbols-outlined text-5xl text-content-muted mb-3">auto_awesome</span>
        <h1 className="text-lg font-semibold text-content-primary">O H.Bot está indisponível no momento</h1>
        <p className="text-sm text-content-muted mt-1 max-w-md">
          O assistente foi desativado pela administração. Tente novamente mais tarde.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] lg:h-screen items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (bootError && !activeConv) {
    return (
      <div className="p-10 text-center">
        <p className="text-red-400">{bootError}</p>
      </div>
    );
  }

  return (
    <div
      className="flex h-[calc(100vh-4rem)] lg:h-screen overflow-hidden"
      data-testid="assistant-page"
    >
      <AssistantConversationSidebar
        conversations={conversations}
        activeId={activeConv?.id ?? null}
        onSelect={(id) => {
          void openConversation(id);
          setConvDrawerOpen(false);
        }}
        onNew={() => {
          void createConversation();
          setConvDrawerOpen(false);
        }}
        onRename={handleRename}
        onArchive={handleArchive}
        isMobileOpen={convDrawerOpen}
        onMobileClose={() => setConvDrawerOpen(false)}
        collapsed={convCollapsed}
        onToggleCollapse={toggleConvCollapsed}
      />

      <AssistantChat
        greetingName={greetingName}
        messages={messages}
        sending={sending}
        thinking={thinking}
        activeTool={activeTool}
        error={error}
        quota={quota}
        assistantModel={activeAssistant?.model}
        assistantModelInfo={activeAssistant?.model_info}
        onSend={handleSend}
        onStop={handleStop}
        attachments={pendingAttachments}
        uploading={uploading}
        onAttachFiles={handleAttachFiles}
        onRemoveAttachment={handleRemoveAttachment}
        onOpenConversations={() => setConvDrawerOpen(true)}
        onOpenMemory={() => setMemoryDrawerOpen(true)}
        activeCompany={activeConv?.active_company ?? null}
        companies={companies}
        onSelectCompany={handleSelectCompany}
        assistantName={activeAssistant?.name ?? activeConv?.agent?.name ?? 'H.Bot'}
        assistantAvatar={activeAssistant?.avatar || activeConv?.agent?.avatar || 'auto_awesome'}
        assistantSubtitle={
          activeAssistant?.description ||
          (isSharepoint
            ? 'Localização de documentos no SharePoint — apoio à decisão'
            : isLegal
              ? 'Análise de contratos — apoio à decisão do Jurídico'
              : isDeck
                ? 'Apresentações de slides com a identidade da empresa'
                : isQuality
                  ? 'Revisão de documentos da Qualidade — apoio à decisão'
                  : 'Seu assistente de IA do h.lab')
        }
        suggestions={
          isSharepoint
            ? SHAREPOINT_SUGGESTIONS
            : isLegal
              ? LEGAL_SUGGESTIONS
              : isDeck
                ? DECK_SUGGESTIONS
                : isQuality
                  ? QUALITY_SUGGESTIONS
                  : undefined
        }
        legalSteps={legalSteps}
        confidential={confidential}
        contracts={contracts}
        onDeleteContract={isLegal || isQuality ? handleDeleteContract : undefined}
        deletingContractId={deletingContractId}
        sharepoint={isSharepoint}
        onReindex={isSharepoint ? handleReindex : undefined}
        onExportDrl={isSharepoint ? handleExportDrl : undefined}
        deck={isDeck}
        quality={isQuality}
        onExportReview={isQuality ? handleExportReview : undefined}
        pendingElicitation={pendingElicitation}
        onElicitationSubmit={handleElicitationSubmit}
      />

      {/* Painel direito: memória + cota. Drawer fechado por padrão (desktop e mobile),
          aberto pelo botão da header — não rouba espaço do chat. */}
      {memoryDrawerOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setMemoryDrawerOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        data-testid="assistant-right-panel"
        className={`w-[320px] max-w-[85vw] border-l border-surface-border bg-surface-secondary flex flex-col h-full z-50 overflow-y-auto transition-transform duration-300 fixed right-0 top-0 bottom-0 ${
          memoryDrawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-2 border-b border-surface-border">
          <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wide pl-2">
            Memória e cota
          </span>
          <button
            type="button"
            onClick={() => setMemoryDrawerOpen(false)}
            aria-label="Fechar painel"
            className="p-2 hover:bg-surface-hover rounded-lg"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <AssistantMemoryPanel
          memory={memory}
          loading={memoryLoading}
          onAdd={handleAddMemory}
          onEdit={handleEditMemory}
          onDelete={handleDeleteMemory}
        />
        <AssistantQuotaSummary quota={quota} />
      </aside>

      {quotaError && (
        <AssistantQuotaModal error={quotaError} quota={quota} onClose={closeQuotaModal} />
      )}
    </div>
  );
};

export default AssistantPage;
